import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Image, Video, Users, Heart, Mail, Calendar,
  Trash2, Star, Eye, Download, RefreshCw, Shield, ChevronDown,
  TrendingUp, DollarSign, UserPlus, FileText, MapPin, Clock, Plus, Edit2, X,
  Award, CheckCircle, XCircle, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  
  // Content states
  const [photos, setPhotos] = useState({ activism: [], partners: [], yvov: [], veterans: [], sleepover: [] });
  const [videos, setVideos] = useState({ activism: [], yvov: [], veterans: [], sleepover: [] });
  const [partners, setPartners] = useState([]);
  const [donations, setDonations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [peaceArchitects, setPeaceArchitects] = useState([]);
  const [mentors, setMentors] = useState([]);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Event management states
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    location: "",
    event_date: "",
    event_time: "",
    image_url: ""
  });
  const [savingEvent, setSavingEvent] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin/login");
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchAllData();
    }
  }, [isAdmin]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        activismPhotos, activismVideos,
        partnerPhotos, partnersData,
        yvovPhotos, yvovVideos,
        veteransPhotos, veteransVideos,
        sleepoverPhotos, sleepoverVideos,
        donationsData, volunteersData, contactsData, eventsData,
        peaceArchitectsData, mentorsData
      ] = await Promise.all([
        axios.get(`${API}/activism/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/activism/videos`).catch(() => ({ data: [] })),
        axios.get(`${API}/partners/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/yvov/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/yvov/videos`).catch(() => ({ data: [] })),
        axios.get(`${API}/veterans/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/veterans/videos`).catch(() => ({ data: [] })),
        axios.get(`${API}/sleepover/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/sleepover/videos`).catch(() => ({ data: [] })),
        axios.get(`${API}/donations`).catch(() => ({ data: [] })),
        axios.get(`${API}/volunteers`).catch(() => ({ data: [] })),
        axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/peace-architect/applications`).catch(() => ({ data: [] })),
        axios.get(`${API}/mentor/applications`).catch(() => ({ data: [] }))
      ]);

      setPhotos({
        activism: activismPhotos.data,
        partners: partnerPhotos.data,
        yvov: yvovPhotos.data,
        veterans: veteransPhotos.data,
        sleepover: sleepoverPhotos.data
      });

      setVideos({
        activism: activismVideos.data,
        yvov: yvovVideos.data,
        veterans: veteransVideos.data,
        sleepover: sleepoverVideos.data
      });

      setPartners(partnersData.data);
      setDonations(donationsData.data);
      setVolunteers(volunteersData.data);
      setContacts(contactsData.data);
      setEvents(eventsData.data);
      setPeaceArchitects(peaceArchitectsData.data);
      setMentors(mentorsData.data);

      // Calculate stats
      const totalPhotos = Object.values({
        activism: activismPhotos.data,
        partners: partnerPhotos.data,
        yvov: yvovPhotos.data,
        veterans: veteransPhotos.data,
        sleepover: sleepoverPhotos.data
      }).reduce((sum, arr) => sum + arr.length, 0);

      const totalVideos = Object.values({
        activism: activismVideos.data,
        yvov: yvovVideos.data,
        veterans: veteransVideos.data,
        sleepover: sleepoverVideos.data
      }).reduce((sum, arr) => sum + arr.length, 0);

      const totalDonations = donationsData.data.reduce((sum, d) => sum + (d.amount || 0), 0);

      setStats({
        totalPhotos,
        totalVideos,
        totalPartners: partnersData.data.length,
        totalDonations,
        totalVolunteers: volunteersData.data.length,
        totalContacts: contactsData.data.length,
        totalEvents: eventsData.data.length,
        totalPeaceArchitects: peaceArchitectsData.data.length,
        totalMentors: mentorsData.data.length
      });

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Event management functions
  const openAddEventDialog = () => {
    setEditingEvent(null);
    setEventForm({
      title: "",
      description: "",
      location: "",
      event_date: "",
      event_time: "",
      image_url: ""
    });
    setEventDialogOpen(true);
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || "",
      description: event.description || "",
      location: event.location || "",
      event_date: event.event_date || "",
      event_time: event.event_time || "",
      image_url: event.image_url || ""
    });
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.event_date || !eventForm.location) {
      toast.error("Please fill in required fields (Title, Date, Location)");
      return;
    }

    setSavingEvent(true);
    try {
      if (editingEvent) {
        // Update existing event
        const response = await axios.put(`${API}/events/${editingEvent.id}`, eventForm);
        setEvents(prev => prev.map(ev => ev.id === editingEvent.id ? response.data : ev));
        toast.success("Event updated successfully");
      } else {
        // Create new event
        const response = await axios.post(`${API}/events`, eventForm);
        setEvents(prev => [response.data, ...prev]);
        toast.success("Event created successfully");
      }
      setEventDialogOpen(false);
      setEditingEvent(null);
    } catch (error) {
      toast.error(editingEvent ? "Failed to update event" : "Failed to create event");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(prev => prev.filter(ev => ev.id !== eventId));
      toast.success("Event deleted");
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleDeletePhoto = async (collection, photoId) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    
    const endpoints = {
      activism: "activism/photos",
      partners: "partners/photos",
      yvov: "yvov/photos",
      veterans: "veterans/photos",
      sleepover: "sleepover/photos"
    };

    try {
      await axios.delete(`${API}/${endpoints[collection]}/${photoId}`);
      setPhotos(prev => ({
        ...prev,
        [collection]: prev[collection].filter(p => p.id !== photoId)
      }));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleDeleteVideo = async (collection, videoId) => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;
    
    const endpoints = {
      activism: "activism/videos",
      yvov: "yvov/videos",
      veterans: "veterans/videos",
      sleepover: "sleepover/videos"
    };

    try {
      await axios.delete(`${API}/${endpoints[collection]}/${videoId}`);
      setVideos(prev => ({
        ...prev,
        [collection]: prev[collection].filter(v => v.id !== videoId)
      }));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleToggleFeatured = async (type, collection, itemId) => {
    const endpoints = {
      photo: {
        activism: "activism/photos",
        partners: "partners/photos",
        yvov: "yvov/photos",
        veterans: "veterans/photos",
        sleepover: "sleepover/photos"
      },
      video: {
        activism: "activism/videos",
        yvov: "yvov/videos",
        veterans: "veterans/videos",
        sleepover: "sleepover/videos"
      }
    };

    try {
      const response = await axios.patch(`${API}/${endpoints[type][collection]}/${itemId}/feature`);
      
      if (type === "photo") {
        setPhotos(prev => ({
          ...prev,
          [collection]: prev[collection].map(p => 
            p.id === itemId ? { ...p, is_featured: response.data.is_featured } : p
          )
        }));
      } else {
        setVideos(prev => ({
          ...prev,
          [collection]: prev[collection].map(v => 
            v.id === itemId ? { ...v, is_featured: response.data.is_featured } : v
          )
        }));
      }
      
      toast.success(response.data.is_featured ? "Marked as featured" : "Removed from featured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;
    
    try {
      await axios.delete(`${API}/partners/${partnerId}`);
      setPartners(prev => prev.filter(p => p.id !== partnerId));
      toast.success("Partner deleted");
    } catch (error) {
      toast.error("Failed to delete partner");
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data.length) {
      toast.error("No data to export");
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} records`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const collectionLabels = {
    activism: "Activism",
    partners: "Partners",
    yvov: "Youth Victory",
    veterans: "Veterans",
    sleepover: "Sleepover"
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0056D2] rounded-xl flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#0F172A]">Admin Dashboard</h1>
                <p className="text-sm text-slate-500">Manage all website content</p>
              </div>
            </div>
            <Button onClick={fetchAllData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalPhotos || 0}</p>
                <p className="text-xs text-slate-500">Photos</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalVideos || 0}</p>
                <p className="text-xs text-slate-500">Videos</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalEvents || 0}</p>
                <p className="text-xs text-slate-500">Events</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalPartners || 0}</p>
                <p className="text-xs text-slate-500">Partners</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">${stats.totalDonations || 0}</p>
                <p className="text-xs text-slate-500">Donations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalVolunteers || 0}</p>
                <p className="text-xs text-slate-500">Volunteers</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{stats.totalContacts || 0}</p>
                <p className="text-xs text-slate-500">Messages</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="overview" className="gap-2">
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="events" className="gap-2">
              <Calendar className="w-4 h-4" />
              Events ({stats.totalEvents || 0})
            </TabsTrigger>
            <TabsTrigger value="photos" className="gap-2">
              <Image className="w-4 h-4" />
              Photos ({stats.totalPhotos || 0})
            </TabsTrigger>
            <TabsTrigger value="videos" className="gap-2">
              <Video className="w-4 h-4" />
              Videos ({stats.totalVideos || 0})
            </TabsTrigger>
            <TabsTrigger value="partners" className="gap-2">
              <Users className="w-4 h-4" />
              Partners ({stats.totalPartners || 0})
            </TabsTrigger>
            <TabsTrigger value="donations" className="gap-2">
              <Heart className="w-4 h-4" />
              Donations
            </TabsTrigger>
            <TabsTrigger value="volunteers" className="gap-2">
              <UserPlus className="w-4 h-4" />
              Volunteers
            </TabsTrigger>
            <TabsTrigger value="peace-architects" className="gap-2">
              <Star className="w-4 h-4" />
              Peace Architects ({stats.totalPeaceArchitects || 0})
            </TabsTrigger>
            <TabsTrigger value="mentors" className="gap-2">
              <Award className="w-4 h-4" />
              Mentors ({stats.totalMentors || 0})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Photos */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-600" />
                  Recent Photos
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {Object.values(photos).flat().slice(0, 8).map((photo, idx) => (
                    <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                {Object.values(photos).flat().length === 0 && (
                  <p className="text-slate-500 text-center py-8">No photos uploaded yet</p>
                )}
              </div>

              {/* Recent Donations */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-600" />
                  Recent Donations
                </h3>
                <div className="space-y-3">
                  {donations.slice(0, 5).map((donation, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-[#0F172A]">{donation.donor_name || "Anonymous"}</p>
                        <p className="text-sm text-slate-500">{donation.donor_email || "No email"}</p>
                      </div>
                      <span className="text-lg font-bold text-green-600">${donation.amount || 0}</span>
                    </div>
                  ))}
                  {donations.length === 0 && (
                    <p className="text-slate-500 text-center py-8">No donations yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#0F172A]">Events ({events.length})</h3>
                <Button onClick={openAddEventDialog} className="bg-[#0056D2] hover:bg-[#0046B2] gap-2">
                  <Plus className="w-4 h-4" />
                  Add Event
                </Button>
              </div>
              {events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map((event) => (
                    <div key={event.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                      {event.image_url && (
                        <div className="aspect-video bg-slate-100">
                          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">{event.title}</h4>
                        <div className="space-y-1 text-sm text-slate-500 mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {event.event_date || "No date set"}
                            {event.event_time && ` at ${event.event_time}`}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {event.location || "No location set"}
                          </div>
                        </div>
                        {event.description && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">{event.description}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditEventDialog(event)}
                            className="flex-1 gap-1"
                          >
                            <Edit2 className="w-3 h-3" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-[#0F172A] mb-2">No Events Yet</h4>
                  <p className="text-slate-500 mb-4">Create your first event to display on the Events page.</p>
                  <Button onClick={openAddEventDialog} className="bg-[#0056D2] hover:bg-[#0046B2] gap-2">
                    <Plus className="w-4 h-4" />
                    Add Your First Event
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6">
            {Object.entries(photos).map(([collection, items]) => (
              <div key={collection} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#0F172A]">
                    {collectionLabels[collection]} Photos ({items.length})
                  </h3>
                </div>
                {items.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {items.map((photo) => (
                      <div key={photo.id} className={`relative aspect-square rounded-xl overflow-hidden group ${photo.is_featured ? 'ring-2 ring-amber-400' : ''}`}>
                        {photo.is_featured && (
                          <div className="absolute top-1 left-1 z-10 bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5" fill="currentColor" />
                          </div>
                        )}
                        <img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleFeatured("photo", collection, photo.id)}
                            className={`p-2 rounded-full ${photo.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-white/20 text-white hover:bg-amber-400 hover:text-amber-900'}`}
                          >
                            <Star className="w-4 h-4" fill={photo.is_featured ? "currentColor" : "none"} />
                          </button>
                          <button
                            onClick={() => { setSelectedItem({ ...photo, type: 'photo' }); setPreviewOpen(true); }}
                            className="p-2 bg-white/20 rounded-full text-white hover:bg-white/40"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePhoto(collection, photo.id)}
                            className="p-2 bg-white/20 rounded-full text-white hover:bg-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No photos in this collection</p>
                )}
              </div>
            ))}
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-6">
            {Object.entries(videos).map(([collection, items]) => (
              <div key={collection} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-[#0F172A]">
                    {collectionLabels[collection]} Videos ({items.length})
                  </h3>
                </div>
                {items.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((video) => (
                      <div key={video.id} className={`relative aspect-video rounded-xl overflow-hidden group ${video.is_featured ? 'ring-2 ring-amber-400' : ''}`}>
                        {video.is_featured && (
                          <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3" fill="currentColor" />
                            Featured
                          </div>
                        )}
                        {video.is_upload ? (
                          <video src={video.video_url} className="w-full h-full object-cover" />
                        ) : (
                          <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                          <p className="text-white font-medium text-center px-4 line-clamp-2">{video.title}</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleFeatured("video", collection, video.id)}
                              className={`p-2 rounded-full ${video.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-white/20 text-white hover:bg-amber-400 hover:text-amber-900'}`}
                            >
                              <Star className="w-4 h-4" fill={video.is_featured ? "currentColor" : "none"} />
                            </button>
                            <button
                              onClick={() => handleDeleteVideo(collection, video.id)}
                              className="p-2 bg-white/20 rounded-full text-white hover:bg-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No videos in this collection</p>
                )}
              </div>
            ))}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0F172A]">Partners ({partners.length})</h3>
              </div>
              {partners.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {partners.map((partner) => (
                    <div key={partner.id} className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl">
                      {partner.logo_url && (
                        <img src={partner.logo_url} alt={partner.name} className="w-16 h-16 object-contain rounded-lg bg-slate-100" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] truncate">{partner.name}</p>
                        <p className="text-sm text-slate-500 capitalize">{partner.partner_type || partner.type}</p>
                      </div>
                      <button
                        onClick={() => handleDeletePartner(partner.id)}
                        className="p-2 text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No partners added yet</p>
              )}
            </div>
          </TabsContent>

          {/* Donations Tab */}
          <TabsContent value="donations">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0F172A]">Donations ({donations.length})</h3>
                <Button onClick={() => exportToCSV(donations, "donations")} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
              {donations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Donor</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((donation, idx) => (
                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                          <td className="py-3 px-4 font-medium text-[#0F172A]">{donation.donor_name || "Anonymous"}</td>
                          <td className="py-3 px-4 text-slate-600">{donation.donor_email || "-"}</td>
                          <td className="py-3 px-4 font-bold text-green-600">${donation.amount || 0}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${donation.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {donation.status || "pending"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {donation.created_at ? new Date(donation.created_at).toLocaleDateString() : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No donations yet</p>
              )}
            </div>
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0F172A]">Volunteers ({volunteers.length})</h3>
                <Button onClick={() => exportToCSV(volunteers, "volunteers")} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
              {volunteers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Phone</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Interests</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {volunteers.map((volunteer, idx) => (
                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                          <td className="py-3 px-4 font-medium text-[#0F172A]">{volunteer.name}</td>
                          <td className="py-3 px-4 text-slate-600">{volunteer.email}</td>
                          <td className="py-3 px-4 text-slate-600">{volunteer.phone || "-"}</td>
                          <td className="py-3 px-4 text-slate-600 text-sm">
                            {Array.isArray(volunteer.interests) ? volunteer.interests.join(", ") : volunteer.interests || "-"}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {volunteer.created_at ? new Date(volunteer.created_at).toLocaleDateString() : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No volunteer signups yet</p>
              )}
            </div>
          </TabsContent>

          {/* Peace Architects Tab */}
          <TabsContent value="peace-architects">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Peace Architect Applications ({peaceArchitects.length})
                </h3>
                <Button onClick={() => exportToCSV(peaceArchitects, "peace-architects")} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
              {peaceArchitects.length > 0 ? (
                <div className="space-y-4">
                  {peaceArchitects.map((app, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-[#0F172A]">{app.first_name} {app.last_name}</h4>
                            <p className="text-sm text-slate-500">{app.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {app.status?.charAt(0).toUpperCase() + app.status?.slice(1) || 'Pending'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-slate-500">Phone:</span>
                          <p className="font-medium text-[#0F172A]">{app.phone || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Age:</span>
                          <p className="font-medium text-[#0F172A]">{app.age || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Neighborhood:</span>
                          <p className="font-medium text-[#0F172A]">{app.neighborhood || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Applied:</span>
                          <p className="font-medium text-[#0F172A]">
                            {app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}
                          </p>
                        </div>
                      </div>
                      {app.why_interested && (
                        <div className="mb-3">
                          <span className="text-sm text-slate-500">Why interested:</span>
                          <p className="text-sm text-[#0F172A] mt-1 bg-slate-50 p-3 rounded-lg">{app.why_interested}</p>
                        </div>
                      )}
                      {app.leadership_experience && (
                        <div className="mb-3">
                          <span className="text-sm text-slate-500">Leadership Experience:</span>
                          <p className="text-sm text-[#0F172A] mt-1 bg-slate-50 p-3 rounded-lg">{app.leadership_experience}</p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-600 hover:bg-green-50"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/peace-architect/applications/${app.id}/status?status=approved`);
                              setPeaceArchitects(prev => prev.map(a => a.id === app.id ? {...a, status: 'approved'} : a));
                              toast.success("Application approved");
                            } catch (e) { toast.error("Failed to update status"); }
                          }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/peace-architect/applications/${app.id}/status?status=rejected`);
                              setPeaceArchitects(prev => prev.map(a => a.id === app.id ? {...a, status: 'rejected'} : a));
                              toast.success("Application rejected");
                            } catch (e) { toast.error("Failed to update status"); }
                          }}
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-slate-600 hover:bg-slate-50 ml-auto"
                          onClick={async () => {
                            if (!window.confirm("Delete this application?")) return;
                            try {
                              await axios.delete(`${API}/peace-architect/applications/${app.id}`);
                              setPeaceArchitects(prev => prev.filter(a => a.id !== app.id));
                              toast.success("Application deleted");
                            } catch (e) { toast.error("Failed to delete"); }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No Peace Architect applications yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Mentors Tab */}
          <TabsContent value="mentors">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-500" />
                  Mentor Applications ({mentors.length})
                </h3>
                <Button onClick={() => exportToCSV(mentors, "mentors")} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              </div>
              {mentors.length > 0 ? (
                <div className="space-y-4">
                  {mentors.map((app, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-[#0F172A]">{app.first_name} {app.last_name}</h4>
                            <p className="text-sm text-slate-500">{app.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {app.status?.charAt(0).toUpperCase() + app.status?.slice(1) || 'Pending'}
                          </span>
                          {app.background_check_consent && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              BG Check OK
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-slate-500">Phone:</span>
                          <p className="font-medium text-[#0F172A]">{app.phone || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Occupation:</span>
                          <p className="font-medium text-[#0F172A]">{app.occupation || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Organization:</span>
                          <p className="font-medium text-[#0F172A]">{app.organization || '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Experience:</span>
                          <p className="font-medium text-[#0F172A]">{app.years_experience ? `${app.years_experience} years` : '-'}</p>
                        </div>
                      </div>
                      {app.areas_of_expertise && (
                        <div className="mb-3">
                          <span className="text-sm text-slate-500">Areas of Expertise:</span>
                          <p className="text-sm text-[#0F172A] mt-1">{app.areas_of_expertise}</p>
                        </div>
                      )}
                      {app.why_mentor && (
                        <div className="mb-3">
                          <span className="text-sm text-slate-500">Why they want to mentor:</span>
                          <p className="text-sm text-[#0F172A] mt-1 bg-slate-50 p-3 rounded-lg">{app.why_mentor}</p>
                        </div>
                      )}
                      {app.mentoring_experience && (
                        <div className="mb-3">
                          <span className="text-sm text-slate-500">Mentoring Experience:</span>
                          <p className="text-sm text-[#0F172A] mt-1 bg-slate-50 p-3 rounded-lg">{app.mentoring_experience}</p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-600 hover:bg-green-50"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/mentor/applications/${app.id}/status?status=approved`);
                              setMentors(prev => prev.map(a => a.id === app.id ? {...a, status: 'approved'} : a));
                              toast.success("Application approved");
                            } catch (e) { toast.error("Failed to update status"); }
                          }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/mentor/applications/${app.id}/status?status=rejected`);
                              setMentors(prev => prev.map(a => a.id === app.id ? {...a, status: 'rejected'} : a));
                              toast.success("Application rejected");
                            } catch (e) { toast.error("Failed to update status"); }
                          }}
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-slate-600 hover:bg-slate-50 ml-auto"
                          onClick={async () => {
                            if (!window.confirm("Delete this application?")) return;
                            try {
                              await axios.delete(`${API}/mentor/applications/${app.id}`);
                              setMentors(prev => prev.filter(a => a.id !== app.id));
                              toast.success("Application deleted");
                            } catch (e) { toast.error("Failed to delete"); }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No Mentor applications yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedItem?.title || "Preview"}</DialogTitle>
          </DialogHeader>
          {selectedItem?.type === 'photo' && (
            <img src={selectedItem.image_url} alt={selectedItem.title} className="w-full rounded-lg" />
          )}
          {selectedItem?.description && (
            <p className="text-slate-600">{selectedItem.description}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Event" : "Create New Event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="event-title">Event Title *</Label>
              <Input
                id="event-title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Enter event title"
                className="mt-1"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-date">Date *</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="event-time">Time</Label>
                <Input
                  id="event-time"
                  type="time"
                  value={eventForm.event_time}
                  onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="event-location">Location *</Label>
              <Input
                id="event-location"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Enter event location"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="event-description">Description</Label>
              <Textarea
                id="event-description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Describe the event..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="event-image">Image URL (optional)</Label>
              <Input
                id="event-image"
                value={eventForm.image_url}
                onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
              {eventForm.image_url && (
                <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-slate-100">
                  <img src={eventForm.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEventDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={savingEvent}
                className="flex-1 bg-[#0056D2] hover:bg-[#0046B2]"
              >
                {savingEvent ? "Saving..." : (editingEvent ? "Update Event" : "Create Event")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
